/**
 * Log a Care Concierge case that arrived from outside the platform (Feature 106, requirement v4
 * item 8; Feature 127 for who it is about).
 *
 * Record-keeping first: a case logged here carries no payer and is therefore invisible to the
 * balance count. The person is either an existing care seeker — picked from a search that shows
 * their advisor, because two clients can share a name but rarely an advisor — or a typed name
 * and/or email. The case may name the advisor it belongs to, and a person not yet on bQuest may
 * be invited from that advisor; the invitation is the one thing here that emails anyone, and the
 * dialog says so.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import { ManualReferralEntitySelect } from '../manual-referrals/ManualReferralEntitySelect';
import type {
  ManualReferralEntityAdvisor,
  ManualReferralEntitySearchItem,
} from '../../types/manual-referral.type';
import type {
  CaseRequestSource,
  CreateManualCasePayload,
} from '../../services/careConciergeCasesApi';

const SOURCES: Array<{ value: CaseRequestSource; label: string }> = [
  { value: 'DIRECT_ADVISOR_REQUEST', label: 'Direct advisor request' },
  { value: 'SP_NOT_FOUND', label: 'SP-not-found request' },
  { value: 'DISCOVERY_CALL', label: 'Discovery call' },
  { value: 'PAID_CONSULTATION', label: 'Paid concierge consultation' },
];

// A care seeker's one advisor becomes the advisor pick without a second search.
const advisorAsItem = (advisor: ManualReferralEntityAdvisor): ManualReferralEntitySearchItem => ({
  id: advisor.userId,
  kind: 'USER',
  label: advisor.name,
  email: null,
  role: 'FINANCIAL_ADVISOR',
  ...(advisor.firm ? { organizationName: advisor.firm } : {}),
});

type ManualCaseDialogProps = {
  isSaving: boolean;
  onSave: (payload: CreateManualCasePayload) => void;
  onClose: () => void;
};

export const ManualCaseDialog = ({ isSaving, onSave, onClose }: ManualCaseDialogProps) => {
  const [requestSource, setRequestSource] = useState<CaseRequestSource>('DIRECT_ADVISOR_REQUEST');
  const [careSeeker, setCareSeeker] = useState<ManualReferralEntitySearchItem | null>(null);
  const [advisor, setAdvisor] = useState<ManualReferralEntitySearchItem | null>(null);
  const [personName, setPersonName] = useState('');
  const [personEmail, setPersonEmail] = useState('');
  const [inviteToApp, setInviteToApp] = useState(false);
  const [requestReceivedAt, setRequestReceivedAt] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Only someone not yet on bQuest can be invited, and only once there is an address to send to.
  const canInvite = !careSeeker && personEmail.trim().length > 0;
  const inviteEnabled = canInvite && advisor !== null;
  const willInvite = inviteEnabled && inviteToApp;

  const pickCareSeeker = (item: ManualReferralEntitySearchItem) => {
    setCareSeeker(item);
    setError(null);
    if (!advisor && item.advisors?.length === 1) {
      setAdvisor(advisorAsItem(item.advisors[0]));
    }
  };

  const handleSave = () => {
    // A case is about someone. Either identifier will do — a person who reached the team by phone
    // may not have given an email.
    if (!careSeeker && !personName.trim() && !personEmail.trim()) {
      setError('Enter a name or an email for the person this case is about.');
      return;
    }
    if (willInvite && !personName.trim()) {
      setError("An invitation needs the person's name.");
      return;
    }

    onSave({
      requestSource,
      ...(careSeeker
        ? { clientUserId: careSeeker.id }
        : {
            ...(personName.trim() ? { personName: personName.trim() } : {}),
            ...(personEmail.trim() ? { personEmail: personEmail.trim() } : {}),
          }),
      ...(advisor ? { advisorUserId: advisor.id } : {}),
      ...(willInvite ? { inviteToApp: true } : {}),
      ...(requestReceivedAt
        ? { requestReceivedAt: new Date(requestReceivedAt).toISOString() }
        : {}),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    });
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Log a new case</DialogTitle>
          <DialogDescription>
            For requests that reached the team outside the platform. Nothing is emailed and no
            sponsored session is used — unless you choose to invite the person to the app.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="cc-manual-source">Request source</Label>
            <select
              id="cc-manual-source"
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={requestSource}
              onChange={(event) => setRequestSource(event.target.value as CaseRequestSource)}
              disabled={isSaving}
            >
              {SOURCES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <ManualReferralEntitySelect
            label="Existing care seeker"
            searchLabel="Search care seekers"
            entityType="CARE_SEEKER"
            usersOnly
            selected={careSeeker}
            onSelect={pickCareSeeker}
          />
          {careSeeker ? (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0"
              onClick={() => setCareSeeker(null)}
              disabled={isSaving}
            >
              Not them? Clear and add someone else
            </Button>
          ) : (
            <div className="space-y-4 rounded-md border border-dashed border-gray-200 p-3">
              <p className="text-sm text-gray-600">
                Or add someone not on bQuest — a name or an email is enough.
              </p>
              <div>
                <Label htmlFor="cc-manual-name">Name</Label>
                <Input
                  id="cc-manual-name"
                  value={personName}
                  onChange={(event) => {
                    setPersonName(event.target.value);
                    setError(null);
                  }}
                  disabled={isSaving}
                />
              </div>
              <div>
                <Label htmlFor="cc-manual-email">Email</Label>
                <Input
                  id="cc-manual-email"
                  type="email"
                  value={personEmail}
                  onChange={(event) => {
                    setPersonEmail(event.target.value);
                    setError(null);
                  }}
                  disabled={isSaving}
                />
              </div>
            </div>
          )}

          <ManualReferralEntitySelect
            label="Financial advisor"
            searchLabel="Search financial advisors"
            entityType="FINANCIAL_ADVISOR"
            usersOnly
            placeholder="Search by name, email or firm"
            selected={advisor}
            onSelect={(item) => setAdvisor(item)}
          />
          {advisor && (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0"
              onClick={() => setAdvisor(null)}
              disabled={isSaving}
            >
              Clear advisor
            </Button>
          )}

          {canInvite && (
            <div className="rounded-md border border-gray-200 p-3">
              <div className="flex items-start gap-2">
                <Checkbox
                  id="cc-manual-invite"
                  checked={willInvite}
                  onCheckedChange={(checked) => setInviteToApp(checked === true)}
                  disabled={!inviteEnabled || isSaving}
                />
                <div>
                  <Label htmlFor="cc-manual-invite" className="font-medium">
                    Invite to app
                  </Label>
                  <p className="text-xs text-gray-600">
                    {inviteEnabled
                      ? `${advisor?.label}'s invitation will be sent to ${personEmail.trim()}. Signing up links them to that advisor.`
                      : 'Choose the financial advisor the invitation should come from.'}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="cc-manual-received">Request received</Label>
            <Input
              id="cc-manual-received"
              type="date"
              value={requestReceivedAt}
              onChange={(event) => setRequestReceivedAt(event.target.value)}
              disabled={isSaving}
            />
          </div>

          <div>
            <Label htmlFor="cc-manual-notes">Notes</Label>
            <Textarea
              id="cc-manual-notes"
              rows={4}
              maxLength={5000}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              disabled={isSaving}
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving…' : willInvite ? 'Log case and invite' : 'Log case'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
